/**
 * Question service implementation
 *
 * Developed By : Travancore Analytics (2025)
 * Author: Akhil Soman
 * Created on: 04/03/2025
 * Project: Vitromed
 * Revision History:
 * SNo.   | Date        | Author        | Task                | Comments |
 * 1      04/03/2025    Akhil Soman       Creating new File
 */

import api from "./api.ts"; // Import the centralized Axios instance

class QuestionService {
  /**
   * Create a new question
   * @param url - API endpoint
   * @param data - Question data to be created
   * @returns Promise with the response data
   */
  async createQuestion(url: string, data: any) {
    try {
      const response = await api.post(url, data);
      if (response.status === 200 || response.status === 201) {
        return response; // Return the response data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error creating question:", error);
      throw error;
    }
  }

  /**
   * Fetch all questions with pagination support
   * @param url - API endpoint
   * @param params - Pagination parameters (currentPage, perPage)
   * @returns Promise with the response data
   */
  async getAllQuestions(
    url: string,
    params: { currentPage: number; perPage: number }
  ) {
    try {
      const response = await api.get(url, {
        params: {
          page: params.currentPage,
          perPage: params.perPage,
        },
      });
      if (response.status === 200) {
        return response.data; // Return the response data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching questions:", error);
      throw error;
    }
  }

  /**
   * Upload a CSV file to import questions
   * @param url - API endpoint
   * @param file - CSV file to upload
   * @param createdBy - User ID who is uploading the file
   * @returns Promise with the response data
   */
  async uploadCSV(url: string, file: File, createdBy: number) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("CreatedBy", createdBy.toString());

      const response = await api.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.status === 200) {
        return response; // Return the response data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error uploading CSV:", error);
      throw error;
    }
  }

  /**
   * Fetch all enabled questions
   * @param url - API endpoint
   * @returns Promise with the response data
   */
  async getAllEnabledQuestions(url: string) {
    try {
      const response = await api.get(url, {
        params: { status: 1 }, // Pass status as a query parameter
      });
      if (response.status === 200) {
        return response.data?.Questions || []; // Return the questions array or an empty array if not found
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching enabled questions:", error);
      throw error;
    }
  }

  /**
   * Update an existing question
   * @param url - API endpoint
   * @param data - Updated question data
   * @returns Promise with the response data
   */
  async updateQuestion(url: string, data: any) {
    try {
      const response = await api.put(url, data);
      if (response.status === 200) {
        return response; // Return the response data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error updating question:", error);
      throw error;
    }
  }

  /**
   * Delete a question
   * @param url - API endpoint
   * @param questionId - ID of the question to delete
   * @returns Promise with the response data
   */
  async deleteQuestion(url: string, questionId: number) {
    try {
      const response = await api.delete(url, {
        data: { QuestionID: questionId }, // Send questionId in the request body
      });
      if (response.status === 200) {
        return response; // Return the response data
      } else {
        throw new Error(`Unexpected status code: ${response.status}`);
      }
    } catch (error) {
      console.error("Error deleting question:", error);
      throw error;
    }
  }
}

const questionService = new QuestionService();
export default questionService;
